// Color configuration system for different themes
export interface ColorTheme {
  name: string;
  bg1: string;
  bg2: string;
  color1: string;
  color2: string;
  color3: string;
  color4: string;
  color5: string;
  colorInteractive: string;
  circleSize: string;
  blending: string;
}

export const colorThemes: Record<string, ColorTheme> = {
  // Maincard theme - Matching the original design
  maincard: {
    name: 'Maincard',
    bg1: 'rgb(15, 3, 35)',
    bg2: 'rgb(0, 0, 0)',
    color1: '255, 69, 0',
    color2: '255, 165, 0',
    color3: '255, 215, 0',
    color4: '139, 0, 139',
    color5: '255, 20, 147',
    colorInteractive: '255, 140, 0',
    circleSize: '90%',
    blending: 'screen'
  },
  
  // Dark Cyber theme - Cool dark theme
  cyber: {
    name: 'Cyber',
    bg1: 'rgb(5, 5, 15)',
    bg2: 'rgb(0, 0, 5)',
    color1: '0, 255, 255',
    color2: '255, 0, 255',
    color3: '0, 255, 150',
    color4: '255, 100, 0',
    color5: '150, 0, 255',
    colorInteractive: '0, 200, 255',
    circleSize: '85%',
    blending: 'screen'
  },
  
  // Neon theme - Bright and vibrant
  neon: {
    name: 'Neon',
    bg1: 'rgb(10, 0, 20)',
    bg2: 'rgb(0, 0, 0)',
    color1: '255, 0, 255',
    color2: '0, 255, 255',
    color3: '255, 255, 0',
    color4: '0, 255, 0',
    color5: '255, 0, 0',
    colorInteractive: '255, 100, 255',
    circleSize: '80%',
    blending: 'screen'
  },
  
  // Ocean theme - Cool blues
  ocean: {
    name: 'Ocean',
    bg1: 'rgb(0, 50, 100)',
    bg2: 'rgb(0, 20, 40)',
    color1: '0, 150, 255',
    color2: '0, 200, 255',
    color3: '100, 220, 255',
    color4: '0, 100, 200',
    color5: '50, 150, 255',
    colorInteractive: '0, 180, 255',
    circleSize: '85%',
    blending: 'multiply'
  },
  
  // Sunset theme - Warm oranges and reds
  sunset: {
    name: 'Sunset',
    bg1: 'rgb(100, 0, 50)',
    bg2: 'rgb(50, 0, 25)',
    color1: '255, 100, 0',
    color2: '255, 150, 0',
    color3: '255, 200, 0',
    color4: '255, 50, 100',
    color5: '255, 100, 150',
    colorInteractive: '255, 120, 0',
    circleSize: '75%',
    blending: 'overlay'
  },
  
  // Forest theme - Natural greens
  forest: {
    name: 'Forest',
    bg1: 'rgb(0, 50, 0)',
    bg2: 'rgb(0, 25, 0)',
    color1: '0, 255, 100',
    color2: '100, 255, 0',
    color3: '150, 255, 100',
    color4: '0, 200, 50',
    color5: '50, 255, 150',
    colorInteractive: '0, 255, 150',
    circleSize: '80%',
    blending: 'soft-light'
  },
  
  // Purple theme - Elegant purples
  purple: {
    name: 'Purple',
    bg1: 'rgb(30, 0, 60)',
    bg2: 'rgb(10, 0, 20)',
    color1: '150, 0, 255',
    color2: '200, 100, 255',
    color3: '255, 150, 255',
    color4: '100, 0, 200',
    color5: '255, 100, 255',
    colorInteractive: '180, 50, 255',
    circleSize: '85%',
    blending: 'screen'
  },
  
  // Dark theme - Minimalist dark
  dark: {
    name: 'Dark',
    bg1: 'rgb(10, 10, 10)',
    bg2: 'rgb(0, 0, 0)',
    color1: '255, 255, 255',
    color2: '200, 200, 200',
    color3: '150, 150, 150',
    color4: '100, 100, 100',
    color5: '255, 255, 255',
    colorInteractive: '255, 255, 255',
    circleSize: '70%',
    blending: 'normal'
  }
};

// Current theme state
let currentTheme: string = 'maincard';

export const getCurrentTheme = (): string => currentTheme;

export const setCurrentTheme = (themeName: string): void => {
  if (colorThemes[themeName]) {
    currentTheme = themeName;
    applyThemeToDocument();
  }
};

export const getCurrentThemeConfig = (): ColorTheme => {
  return colorThemes[currentTheme];
};

// Apply theme to document CSS variables
export const applyThemeToDocument = (): void => {
  const theme = getCurrentThemeConfig();
  const root = document.documentElement;
  
  root.style.setProperty('--color-bg1', theme.bg1);
  root.style.setProperty('--color-bg2', theme.bg2);
  root.style.setProperty('--color1', theme.color1);
  root.style.setProperty('--color2', theme.color2);
  root.style.setProperty('--color3', theme.color3);
  root.style.setProperty('--color4', theme.color4);
  root.style.setProperty('--color5', theme.color5);
  root.style.setProperty('--color-interactive', theme.colorInteractive);
  root.style.setProperty('--circle-size', theme.circleSize);
  root.style.setProperty('--blending', theme.blending);
};

// Theme switcher component props
export interface ThemeSwitcherProps {
  onThemeChange?: (themeName: string) => void;
  className?: string;
}

// Get all available themes
export const getAvailableThemes = (): ColorTheme[] => {
  return Object.values(colorThemes);
};
